import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, DONOR_SOURCE_NOTE } from "@/lib/api";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, AsOfBar } from "@/components/PageHeader";
import FreshnessPanel from "@/components/FreshnessPanel";
import { TIER_STYLES } from "@/lib/api";
import { Search, Network, FileCheck2, Landmark, HandCoins, HeartHandshake, Info, Award } from "lucide-react";

const TIER_LABELS = {
  reviewed: "Reviewed", confirmed: "Confirmed", high: "High", review: "Review", candidate: "Candidate", rejected: "Rejected",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/v1/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/v1/freshness").then((r) => setJobs(r.data.jobs)).catch(() => {});
  }, []);

  const cards = stats ? [
    { key: "it", icon: FileCheck2, label: "NGOs with 80G / 12A", value: stats.with_it, hint: "Income Tax registered" },
    { key: "fcra", icon: Landmark, label: "NGOs with FCRA", value: stats.with_fcra, hint: "MHA registered" },
    { key: "csr1", icon: HandCoins, label: "NGOs with CSR-1", value: stats.with_csr1, hint: "MCA CSR-1" },
    { key: "donors", icon: HeartHandshake, label: "NGOs with Donor info", value: stats.with_donors, hint: "at least 1 donor" },
    { key: "guidestar", icon: Award, label: "NGOs with GuideStar ratings", value: stats.with_guidestar, hint: "certified on GuideStar India" },
  ] : [];

  return (
    <div>
      <PageHeader
        eyebrow={`Welcome, ${user?.name?.split(" ")[0] || "analyst"}`}
        title="Crosswalk Overview"
        subtitle="Live snapshot of the RADAR entity map and the freshness of every source registry."
      >
        <button
          data-testid="go-lookup-btn"
          onClick={() => navigate("/app/search")}
          className="inline-flex items-center gap-2 rounded-full bg-blue-500 px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-blue-400 transition-colors duration-150"
        >
          <Search className="w-4 h-4" /> Search NGOs
        </button>
      </PageHeader>

      <div className="mb-6"><AsOfBar asOf={stats?.as_of} /></div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 mb-8">
        {cards.map((c, i) => (
          <button
            key={c.label}
            data-testid={`stat-card-${c.key}`}
            onClick={() => navigate(`/app/mapping?has=${c.key}`)}
            className="group text-left rounded-2xl border border-slate-800 bg-slate-900/70 p-5 backdrop-blur-sm animate-fade-up hover:border-blue-700/60 hover:bg-slate-900 transition-colors duration-150"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <c.icon className="w-5 h-5 text-blue-400 mb-3" />
            <div className="font-mono text-2xl font-bold text-slate-100">{(c.value ?? 0).toLocaleString()}</div>
            <div className="mt-1 text-sm font-medium text-slate-300 flex items-center gap-1.5">
              {c.label}
              {c.key === "donors" && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      data-testid="donor-source-info"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex text-emerald-600 hover:text-emerald-800 transition-colors cursor-help"
                    >
                      <Info className="w-3.5 h-3.5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">{DONOR_SOURCE_NOTE}</TooltipContent>
                </Tooltip>
              )}
            </div>
            <div className="text-[11px] text-slate-500">{c.hint}</div>
            <div className="mt-2 text-[11px] font-semibold text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-150">View list →</div>
          </button>
        ))}
      </div>

      <h3 className="text-lg font-semibold text-slate-200 mb-3 flex items-center gap-2">
        <Network className="w-4 h-4 text-slate-400" /> Proposed links by confidence tier
      </h3>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-6 mb-10">
        {stats && Object.entries(stats.links_by_tier).map(([tier, count]) => (
          <div key={tier} className={`rounded-xl border p-4 ${TIER_STYLES[tier]}`}>
            <div className="font-mono text-xl font-bold">{count.toLocaleString()}</div>
            <div className="text-[11px] uppercase tracking-wider opacity-80">{TIER_LABELS[tier]}</div>
          </div>
        ))}
      </div>

      <h3 className="text-lg font-semibold text-slate-200 mb-3">Registry freshness</h3>
      <FreshnessPanel jobs={jobs} />
    </div>
  );
}
